import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import AuthStore from 'auth-store';
import { useApiLoading } from 'react-reqq-lite';
import { useHistory, useLocation } from 'react-router-dom';

export const useDelayedApiLoading = (constant, type, DELAY = 400) => {
  const isLoading = useApiLoading(constant, type);
  const [loading, setLoading] = useState(isLoading);
  const timeout = useRef();
  useEffect(() => {
    clearTimeout(timeout.current);
    if (isLoading) {
      timeout.current = setTimeout(() => {
        setLoading(true);
      }, DELAY);
      return () => {
        clearTimeout(timeout.current);
      };
    }
    setLoading(false);
    return () => {};
  }, [isLoading, setLoading, DELAY]);
  return loading;
};

export const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const elementToObserve = ref.current;
    const resizeObserver = new ResizeObserver((entries) =>
      entries.forEach((entry) => setDimensions(entry.contentRect))
    );

    resizeObserver.observe(elementToObserve);
    return () => resizeObserver.unobserve(elementToObserve);
  }, [ref]);

  return dimensions;
};

const BASE_URL = process.env.REACT_APP_END_POINT;

export const useDownloadFile = ({
  downloadUrl = '',
  fileName = '',
  fileExtension = 'csv',
}) => {
  const token = AuthStore.get('_token');
  const [isLoading, setIsLoading] = useState(false);
  const downloadFile = useCallback(async () => {
    if (downloadUrl) {
      setIsLoading(true);
      const response = await fetch(`${BASE_URL}/${downloadUrl}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.setAttribute('hidden', '');
      downloadLink.setAttribute('href', url);
      downloadLink.setAttribute('download', `${fileName}.${fileExtension}`);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      setIsLoading(false);
    }
  }, [downloadUrl, fileName, fileExtension, token]);
  return [downloadFile, isLoading];
};

export const useOutsideClick = (componentRef, active, callback) => {
  const handleClickOutside = useCallback(
    (event) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target)
      ) {
        callback();
        document.removeEventListener('click', handleClickOutside);
      }
    },
    [componentRef, callback]
  );

  const cleanListener = useCallback(() => {
    document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (active) {
      document.addEventListener('click', handleClickOutside);
    } else {
      cleanListener();
    }
  }, [active, handleClickOutside, cleanListener]);

  return cleanListener;
};

export const useRouterData = (fallbackData = {}) => {
  const isFirstMountRef = useRef(true);
  const location = useLocation();
  const history = useHistory();

  const push = (url, data = {}) => {
    history.push(url, data);
  };

  const data = useMemo(() => {
    if (isFirstMountRef.current && !!location?.state) {
      isFirstMountRef.current = false;
      return location?.state;
    }
    if (isEmpty(fallbackData) && !!location?.state) {
      return location?.state;
    }
    return fallbackData;
  }, [location, fallbackData]);

  return [data, push];
};
